import { Link } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { MapPin, Users, Globe, Calendar, Heart, Zap, Laptop, ArrowRight } from "lucide-react";
import aboutTeamTech from "@/assets/about-team-tech.jpg";
import aboutPartnership from "@/assets/about-partnership.jpg";
import aboutRemoteWork from "@/assets/about-remote-work.jpg";
import aboutGlobalReach from "@/assets/about-global-reach.jpg";

const About = () => {
  const milestones = [
    { year: "2020", title: "Founded in the UK", description: "Started with a vision to revolutionize event photography" },
    { year: "2021", title: "First 1,000 Events", description: "Partnered with local running clubs and sports events" },
    { year: "2022", title: "AI Launch", description: "Introduced facial recognition and bib detection" },
    { year: "2023", title: "Global Expansion", description: "Expanded to serve photographers across 30+ countries" },
    { year: "2024", title: "10K+ Events", description: "Reached milestone of powering over 10,000 events worldwide" },
  ];

  const values = [
    { icon: Zap, title: "Technical Excellence", description: "Cloud computing and AI expertise at our core" },
    { icon: Users, title: "Community Partnership", description: "Built hand-in-hand with photographers and event organizers" },
    { icon: Heart, title: "User-Focused Innovation", description: "Every feature solves a real problem we've heard firsthand" },
  ];

  return (
    <div className="min-h-screen bg-background">
      <AppHeader variant="marketing" />
      
      {/* Hero Section with Background Image */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src={aboutTeamTech} alt="Tech team working" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-hawk-navy/95 via-hawk-navy/85 to-hawk-navy/70" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center text-white">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
              <Laptop size={16} className="text-hawk-orange" />
              <span className="text-sm font-medium">Remote-First, UK Based</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold mb-6">
              A Small Team with a <span className="text-gradient-primary">Global Vision</span>
            </h1>
            <p className="text-lg md:text-xl text-white/80">
              Over the past 5 years, we've grown from a small UK startup into a platform trusted by photographers and event organizers around the world.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Partnership Image Banner */}
            <div className="relative rounded-3xl overflow-hidden mb-16 shadow-2xl">
              <img src={aboutPartnership} alt="Event photographer in action" className="w-full h-80 object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="text-white text-lg font-medium drop-shadow-lg">Understanding the real challenges photographers face in the field</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <h2 className="text-3xl md:text-4xl font-display font-bold mb-6">Our Story</h2>
                <div className="space-y-4 text-muted-foreground">
                  <p>
                    Photohawk was founded by a team of cloud computing and AI specialists who saw an opportunity to transform event photography. We partnered closely with photographers, event organizers, and participants to truly understand their frustrations.
                  </p>
                  <p>
                    In 2020, as a distributed team across the UK, we combined our technical expertise with real-world insights from the photography community. The result? A platform that leverages cutting-edge AI to automate the tedious parts of event photography, so photographers can focus on what they do best – capturing amazing moments.
                  </p>
                  <p>
                    Five years later, we're still a tight-knit remote team of engineers and industry partners, now serving photographers and events across the globe. From local park runs to international marathons, Photohawk has become the go-to platform for sharing and selling event photos.
                  </p>
                </div>
              </div>
              {/* Remote Work Image */}
              <div className="relative">
                <img src={aboutRemoteWork} alt="Remote team member working" className="w-full rounded-2xl shadow-xl" />
                <div className="absolute -bottom-4 -right-4 bg-hawk-orange text-white px-4 py-2 rounded-lg font-semibold shadow-lg">
                  Remote-First Team
                </div>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-card rounded-2xl p-6 text-center shadow-lg">
                <Globe className="w-10 h-10 text-hawk-orange mx-auto mb-3" />
                <div className="text-3xl font-display font-bold text-foreground">30+</div>
                <div className="text-sm text-muted-foreground">Countries</div>
              </div>
              <div className="bg-card rounded-2xl p-6 text-center shadow-lg">
                <Users className="w-10 h-10 text-hawk-orange mx-auto mb-3" />
                <div className="text-3xl font-display font-bold text-foreground">10K+</div>
                <div className="text-sm text-muted-foreground">Photographers</div>
              </div>
              <div className="bg-card rounded-2xl p-6 text-center shadow-lg">
                <Calendar className="w-10 h-10 text-hawk-orange mx-auto mb-3" />
                <div className="text-3xl font-display font-bold text-foreground">5</div>
                <div className="text-sm text-muted-foreground">Years Strong</div>
              </div>
              <div className="bg-card rounded-2xl p-6 text-center shadow-lg">
                <Laptop className="w-10 h-10 text-hawk-orange mx-auto mb-3" />
                <div className="text-3xl font-display font-bold text-foreground">100%</div>
                <div className="text-sm text-muted-foreground">Remote Team</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-12">Our Journey</h2>
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-hawk-orange/30 -translate-x-1/2" />
              
              {milestones.map((milestone, index) => (
                <div 
                  key={milestone.year}
                  className={`relative flex items-center gap-8 mb-8 ${
                    index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                  }`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? "md:text-right" : "md:text-left"} pl-12 md:pl-0`}>
                    <div className="bg-card rounded-xl p-6 shadow-lg inline-block">
                      <div className="text-hawk-orange font-display font-bold text-xl mb-1">{milestone.year}</div>
                      <div className="font-semibold text-foreground mb-1">{milestone.title}</div>
                      <div className="text-sm text-muted-foreground">{milestone.description}</div>
                    </div>
                  </div>
                  <div className="absolute left-4 md:left-1/2 w-4 h-4 bg-hawk-orange rounded-full -translate-x-1/2 shadow-lg" />
                  <div className="flex-1 hidden md:block" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Global Reach Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={aboutGlobalReach} alt="Global network" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-hawk-navy/80" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-6">Global Reach, Local Expertise</h2>
            <p className="text-lg text-white/80 mb-8">
              From our UK base, we've expanded to serve photographers and events across 30+ countries. Our cloud infrastructure ensures fast, reliable service wherever you are in the world.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {["Europe", "North America", "Asia Pacific", "South America", "Africa"].map((region) => (
                <span key={region} className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium border border-white/20">
                  {region}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-12">What We Stand For</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {values.map((value) => (
                <div key={value.title} className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-hawk-orange to-hawk-gold flex items-center justify-center mx-auto mb-4">
                    <value.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-display font-semibold text-lg mb-2">{value.title}</h3>
                  <p className="text-muted-foreground text-sm">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>


      <Footer />
    </div>
  );
};

export default About;
